import { useEffect, useState } from 'react';
import { APP_CONFIG } from '../config';

const SPEC_MODE = {
  PUBLIC: 'public',
  INTERNAL: 'internal',
  SPA: 'spa',
};

const SWAGGER_OBO_SCOPE = 'api://0be9fa15-ba80-4fbb-8399-2fdc49ce7846/.default';

function normalizeOnlineSwaggerSecurity(specMode, spec) {
  if (!spec || specMode === SPEC_MODE.SPA) {
    return spec;
  }

  const isSwagger2 = typeof spec.swagger === 'string' && spec.swagger.startsWith('2');
  const isOpenApi3 = typeof spec.openapi === 'string' && spec.openapi.startsWith('3');

  if (isSwagger2) {
    const currentDefs = spec.securityDefinitions || {};
    if (Object.keys(currentDefs).length > 0) {
      return spec;
    }

    return {
      ...spec,
      securityDefinitions: {
        bearerAuth: {
          type: 'apiKey',
          name: 'Authorization',
          in: 'header',
          description: 'Input your bearer token in the format: Bearer <token>',
        },
      },
      security: Array.isArray(spec.security) && spec.security.length > 0
        ? spec.security
        : [{ bearerAuth: [] }],
    };
  }

  if (isOpenApi3) {
    const currentSchemes = spec.components?.securitySchemes || {};
    if (Object.keys(currentSchemes).length > 0) {
      return spec;
    }

    return {
      ...spec,
      components: {
        ...(spec.components || {}),
        securitySchemes: {
          bearerAuth: {
            type: 'http',
            scheme: 'bearer',
            bearerFormat: 'JWT',
            description: 'Input your bearer token',
          },
        },
      },
      security: Array.isArray(spec.security) && spec.security.length > 0
        ? spec.security
        : [{ bearerAuth: [] }],
    };
  }

  return spec;
}

function getSwaggerUrl(specMode) {
  const apiBaseUrl = APP_CONFIG.API_BASE_URL;

  if (specMode === SPEC_MODE.INTERNAL) {
    return `${apiBaseUrl}/swagger/docs/v1?internal=1`;
  }

  if (specMode === SPEC_MODE.SPA) {
    return `${apiBaseUrl}/api-docs.json`;
  }

  return `${apiBaseUrl}/swagger/docs/v1`;
}

async function getSwaggerAuthToken(specMode, userAccessToken) {
  if (!userAccessToken) {
    return '';
  }

  if (specMode === SPEC_MODE.SPA) {
    return userAccessToken;
  }

  const oboResponse = await fetch(`${APP_CONFIG.API_BASE_URL}/obo/token`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${userAccessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ scope: SWAGGER_OBO_SCOPE }),
  });

  if (!oboResponse.ok) {
    const errorBody = await oboResponse.text();
    throw new Error(`Failed to acquire Swagger OBO token: ${oboResponse.status} ${errorBody}`);
  }

  const oboResult = await oboResponse.json();
  return oboResult?.accessToken || '';
}

export function useSwaggerSpec(specMode, getAccessToken) {
  const [swaggerAuthToken, setSwaggerAuthToken] = useState('');
  const [swaggerSpec, setSwaggerSpec] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadSwagger = async () => {
      setIsLoading(true);
      setSwaggerSpec(null);

      try {
        let userAccessToken = '';
        let swaggerOperationToken = '';

        try {
          userAccessToken = await getAccessToken();
          swaggerOperationToken = await getSwaggerAuthToken(specMode, userAccessToken);
          if (!isCancelled) {
            setSwaggerAuthToken(swaggerOperationToken || '');
          }
        } catch (tokenError) {
          if (tokenError.message !== 'SESSION_EXPIRED') {
            console.warn('[SwaggerPage] Token acquisition failed:', tokenError.message);
          }
          if (!isCancelled) {
            setSwaggerAuthToken('');
          }
        }

        const requestHeaders = userAccessToken ? { Authorization: `Bearer ${userAccessToken}` } : {};
        const specResponse = await fetch(getSwaggerUrl(specMode), {
          headers: requestHeaders,
        });

        if (!specResponse.ok) {
          throw new Error(`Failed to fetch swagger spec: ${specResponse.status}`);
        }

        const spec = await specResponse.json();
        const normalizedSpec = normalizeOnlineSwaggerSecurity(specMode, spec);
        if (!isCancelled) {
          setSwaggerSpec(normalizedSpec);
        }
      } catch (error) {
        console.error('[SwaggerPage] Failed to initialize Swagger:', error);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSwagger();

    return () => {
      isCancelled = true;
    };
  }, [getAccessToken, specMode]);

  return {
    isLoading,
    swaggerAuthToken,
    swaggerSpec,
  };
}